import { Link } from "react-router-dom";
import { ArrowRight, Receipt, Landmark, Headset, Wallet, FolderLock, CalendarCheck, Sparkles, ShieldCheck, MousePointerClick, TrendingUp, CheckCircle2 } from "lucide-react";
import { useSite } from "@/context/Session";

const FEATURES = [
  [MousePointerClick, "Facilidade de uso", "Tudo em um painel claro, no computador ou no celular."],
  [Receipt, "Notas fiscais", "Organize notas emitidas com XML e PDF. Emissão integrada à NFS-e Nacional em preparação."],
  [Landmark, "Impostos organizados", "Apurações e guias oficiais publicadas pelo contador, com comprovantes."],
  [Headset, "Atendimento contábil", "Chat e chamados com a organização contábil parceira vinculada."],
  [Wallet, "Gestão financeira", "Receitas, despesas, contas a pagar e a receber em tempo real."],
  [FolderLock, "Controle de documentos", "Armazenamento privado, com acesso apenas a quem você autorizar."],
  [CalendarCheck, "Calendário de obrigações", "PGDAS-D, DCTFWeb, eSocial e mais, com responsável e evidência."],
  [Sparkles, "Normah AI", "Assistente inteligente que explica termos, resume pendências e orienta."],
  [ShieldCheck, "Segurança e LGPD", "Isolamento por empresa, auditoria de operações e exportação de dados."],
];

export const Img = ({ src, alt, className = "" }) => (
  <img src={src} alt={alt} loading="lazy" decoding="async" className={`h-full w-full object-cover ${className}`} />
);

export default function Home() {
  const s = useSite();
  const im = s.images || {};
  return (
    <main>
      <section className="relative overflow-hidden">
        <div className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#81D8D0]/30 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-14 lg:grid-cols-2 lg:px-8 lg:py-24">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[#12343B] shadow-sm">
              <span className="h-2 w-2 rounded-full bg-[#00FF40]" /> {s.slogan || "Sua contabilidade inteligente, simples e humana."}
            </span>
            <h1 data-testid="hero-title" className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-[#12343B] sm:text-5xl lg:text-6xl">
              {s.hero_title || "Sua contabilidade mais simples, inteligente e digital."}
            </h1>
            <p className="mt-6 max-w-xl text-base text-[#587178] md:text-lg">{s.hero_subtitle}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/planos" className="btn-primary h-12 px-6" data-testid="hero-plans-btn">Conhecer os planos <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/como-funciona" className="btn-accent h-12 px-6" data-testid="hero-explore-btn">Explorar a plataforma</Link>
            </div>
            {!s.accounting_service_active && <p className="mt-6 max-w-lg text-xs text-[#587178]">{s.accounting_service_notice}</p>}
          </div>
          <div className="relative fade-up" style={{ animationDelay: ".15s" }}>
            <div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-[0_30px_60px_-20px_rgba(18,52,59,0.35)]">
              <Img src={im.hero || "/img/dashboard_team.webp"} alt="Equipe de contabilidade em escritório moderno (imagem ilustrativa)" />
            </div>
            <div className="absolute -bottom-6 left-4 card-soft flex items-center gap-3 p-4 sm:left-[-24px]">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#00FF40]"><TrendingUp className="h-5 w-5 text-[#12343B]" /></span>
              <div><p className="text-xs text-[#587178]">Tudo em um só lugar</p><p className="font-bold text-[#12343B]">Financeiro · Fiscal · Documentos</p></div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <p className="eyebrow">O que a plataforma faz</p>
        <h2 className="mt-2 max-w-2xl text-3xl font-bold text-[#12343B]">Sua empresa organizada, com clareza fiscal e sem jargão.</h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(([Icon, t, d], i) => (
            <div key={t} className="card-soft group p-6 transition-transform duration-300 hover:-translate-y-1 fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#81D8D0]/25 text-[#12343B] transition-colors group-hover:bg-[#81D8D0]"><Icon className="h-5 w-5" /></span>
              <h3 className="mt-4 text-lg font-bold text-[#12343B]">{t}</h3>
              <p className="mt-1 text-sm text-[#587178]">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <Split img={im.how || "/img/team_office.webp"} eyebrow="Como funciona" title="Três passos para tirar a contabilidade da gaveta."
        items={["Crie sua conta e cadastre sua empresa com validação de CNPJ.", "Envie documentos, lance receitas e despesas e organize notas.", "Autorize uma organização contábil parceira para atender sua empresa."]} />
      <Split reverse img={im.service || "/img/accountant_client.webp"} eyebrow="Atendimento contábil" title="Gente de verdade, com responsabilidade técnica identificada."
        items={["Conversas e chamados ficam registrados na plataforma.", "O contador publica apurações e anexa guias geradas em sistemas oficiais.", "Registro CRC exibido somente após conferência válida."]} />
      <Split img={im.benefits || "/img/reports_analysis.webp"} eyebrow="Benefícios" title="Decisões melhores com relatórios gerenciais claros."
        items={["Faturamento, despesas e vencimentos em um painel.", "Alertas de obrigações e documentos pendentes.", "Exportação dos seus dados a qualquer momento (LGPD)."]} />

      <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-[#12343B] p-10 md:p-14">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#81D8D0]/25 blur-3xl" />
          <h2 className="relative max-w-xl text-3xl font-bold text-white">Pronto para simplificar a contabilidade da sua empresa?</h2>
          <div className="relative mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/sign-up" className="btn-accent h-12 px-6" data-testid="cta-signup">Criar minha conta</Link>
            <Link to="/planos" className="inline-flex h-12 items-center justify-center rounded-[10px] border border-white/30 px-6 font-semibold text-white hover:bg-white/10">Ver planos</Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export function Split({ img, eyebrow, title, items, reverse }) {
  return (
    <section className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-14 lg:grid-cols-2 lg:px-8">
      <div className={`aspect-[4/3] overflow-hidden rounded-3xl ${reverse ? "lg:order-2" : ""}`}><Img src={img} alt={`${eyebrow} (imagem ilustrativa)`} /></div>
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="mt-2 text-3xl font-bold text-[#12343B]">{title}</h2>
        <ul className="mt-6 space-y-4">
          {items.map((t) => <li key={t} className="flex gap-3 text-[#587178]"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#2a8f86]" />{t}</li>)}
        </ul>
      </div>
    </section>
  );
}
